/* eslint-disable react-refresh/only-export-components */
import { createFileRoute, useParams, useSearch } from '@tanstack/react-router';
import { z } from 'zod';
import { useAuditTypes } from '@/app/(client)/audits/-hooks/useAuditsQuery';
import { PageContainer } from '@/components/shared/PageContainer';
import { Badge } from '@/components/ui/badge';
import {
  useCollaborationStatusOptions,
  useCountryOptions,
  useFacilityProcessOptions,
  useNetworkCompanyOptions,
} from '@/hooks/useDropdowns';
import { useTranslator } from '@/hooks/useTranslator';
import { useSessionStore } from '@/store/sessionStore';
import { AuditDrilldownTable } from '../../../../../-components/audit-compliance/AuditDrilldownTable';
import {
  buildAuditDrilldownFilter,
  parseFilterParam,
} from '../../../../../-utils/drilldownUtils';

export const Route = createFileRoute(
  '/(client)/report/(pages)/(drilldown)/audit-compliance/drilldown/$chartType/',
)({
  validateSearch: z.object({
    filter: z.union([z.string(), z.record(z.unknown())]).optional(),
  }),
  component: AuditDrilldownPage,
});

function AuditDrilldownPage() {
  const { getTranslation: t } = useTranslator({ page: 'auditCompliance' });
  const { chartType } = useParams({ strict: false }) as { chartType: string };
  const search = (useSearch({ strict: false }) ?? {}) as {
    filter?: string | Record<string, unknown>;
  };
  const companyId = useSessionStore(s => s.user?.companyId ?? '');

  const filterObj = parseFilterParam(search.filter);
  const drilldownFilter = buildAuditDrilldownFilter(filterObj);

  // Load options to resolve IDs → display labels (matches old frontend behaviour)
  const { data: supplierOptions = [] } = useNetworkCompanyOptions();
  const { data: collabStatusOptions = [] } = useCollaborationStatusOptions();
  const { data: processOptions = [] } = useFacilityProcessOptions();
  const { data: countryOptions = [] } = useCountryOptions();
  const { data: auditTypesData } = useAuditTypes();
  const auditTypeOptions = (auditTypesData?.data ?? []).map(at => ({
    key: at.id,
    label: at.name,
  }));

  const resolveIds = (
    ids: string[],
    options: { key: string; label: string }[],
  ) => ids.map(id => options.find(o => o.key === id)?.label ?? id);

  const buildFilterChips = (): string[] => {
    const chips: string[] = [];

    const urgency = drilldownFilter.auditFindingUrgency as string | undefined;
    if (urgency) chips.push(t(`drilldown.urgency.${urgency}`) ?? urgency);
    if (drilldownFilter.impactArea)
      chips.push(String(drilldownFilter.impactArea));
    if (drilldownFilter.upcomingAudits)
      chips.push(t('drilldown.chips.upcomingAudits'));
    if (drilldownFilter.startDate && drilldownFilter.endDate) {
      chips.push(
        `${String(drilldownFilter.startDate).slice(0, 10)} – ${String(drilldownFilter.endDate).slice(0, 10)}`,
      );
    }

    const supplierIds =
      (drilldownFilter.supplierCompanyIds as string[] | undefined) ?? [];
    chips.push(...resolveIds(supplierIds, supplierOptions));

    const collabIds =
      (drilldownFilter.collaborationStatusIds as string[] | undefined) ?? [];
    chips.push(...resolveIds(collabIds, collabStatusOptions));

    const processIds =
      (drilldownFilter.facilityProcesses as string[] | undefined) ?? [];
    chips.push(...resolveIds(processIds, processOptions));

    const countryIds =
      (drilldownFilter.countries as string[] | undefined) ?? [];
    chips.push(
      ...resolveIds(
        countryIds,
        countryOptions as { key: string; label: string }[],
      ),
    );

    const auditTypeIds =
      (drilldownFilter.auditTypeIds as string[] | undefined) ?? [];
    chips.push(...resolveIds(auditTypeIds, auditTypeOptions));

    return chips;
  };

  const filterChips = buildFilterChips();
  const chartTitle = t(`drilldown.chartType.${chartType}`) || chartType;

  return (
    <PageContainer className='flex flex-col overflow-hidden'>
      <div className='shrink-0 border-b border-border/60 bg-background px-4 py-4'>
        <h1 className='text-sm font-semibold text-foreground leading-tight'>
          {t('drilldown.headerPrefix')}
          {chartTitle}
        </h1>
        <p className='text-sm text-muted-foreground mt-0.5'>
          {t('drilldown.description')}
        </p>
        <p className='text-xs text-muted-foreground'>
          {t('drilldown.filterNote')}
        </p>
        {filterChips.length > 0 && (
          <div className='flex flex-wrap gap-1.5 mt-2'>
            {filterChips.map(chip => (
              <Badge
                key={chip}
                variant='secondary'
                className='rounded-md text-xs font-normal'
              >
                {chip}
              </Badge>
            ))}
          </div>
        )}
      </div>
      <div className='flex flex-col flex-1 min-h-0 overflow-hidden'>
        <AuditDrilldownTable companyId={companyId} filter={drilldownFilter} />
      </div>
    </PageContainer>
  );
}
